import type {
  IssueSecondarySecurityAlertNode,
  IssueSecondarySecurityAlertOwnerNode,
  IssueSecondarySecurityAlertRepositoryNode,
} from '../components/header/linked-security-alert-query-types'
import type {LinkedSecurityAlertsFragment} from '../components/header/LinkedSecurityAlerts'

type IssueData = NonNullable<LinkedSecurityAlertsFragment>
type LinkedSecurityAlertNode = Pick<IssueSecondarySecurityAlertNode, 'number' | 'status' | 'title'> & {
  readonly repository:
    | (Pick<IssueSecondarySecurityAlertRepositoryNode, 'name'> & {
        readonly owner: Pick<IssueSecondarySecurityAlertOwnerNode, 'login'>
      })
    | null
}

export type LinkedSecurityAlert = {
  alert: LinkedSecurityAlertNode
  id: string
}

export type LinkedSecurityAlertsData = {
  fixedAlerts: LinkedSecurityAlert[]
  openAlerts: LinkedSecurityAlert[]
  dismissedAlerts: LinkedSecurityAlert[]
  alerts: LinkedSecurityAlert[]
}

export const getLinkedSecurityAlerts = (data: IssueData): LinkedSecurityAlertsData => {
  const fixedAlerts: LinkedSecurityAlert[] = []
  const openAlerts: LinkedSecurityAlert[] = []
  const dismissedAlerts: LinkedSecurityAlert[] = []
  const issueRepositoryName = data.repository?.nameWithOwner?.toLocaleLowerCase() ?? ''

  for (const alert of data.securityAlerts?.nodes ?? []) {
    if (!alert || !alert.repository?.nameWithOwner || !alert.number) continue

    const isExternal = alert.repository.nameWithOwner.toLocaleLowerCase() !== issueRepositoryName
    const alertWithId: LinkedSecurityAlert = {
      alert,
      id: `${alert.repository.nameWithOwner}/${alert.number}`,
    }

    // We add external alerts to the end of each list
    switch (alert.status) {
      case 'FIXED':
        if (isExternal) {
          fixedAlerts.push(alertWithId)
        } else {
          fixedAlerts.unshift(alertWithId)
        }
        break
      case 'OPEN':
        if (isExternal) {
          openAlerts.push(alertWithId)
        } else {
          openAlerts.unshift(alertWithId)
        }
        break
      case 'DISMISSED':
        if (isExternal) {
          dismissedAlerts.push(alertWithId)
        } else {
          dismissedAlerts.unshift(alertWithId)
        }
        break
      default:
        break
    }
  }

  // Combine all alerts into one list
  const alerts: LinkedSecurityAlert[] = [...fixedAlerts, ...openAlerts, ...dismissedAlerts]
  return {fixedAlerts, openAlerts, dismissedAlerts, alerts}
}
